import React, { createContext, useContext, useEffect, useState } from 'react';
import { createTheme } from '@mui/material/styles';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Light theme configuration
const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2',
      light: '#42a5f5',
      dark: '#1565c0',
    },
    secondary: {
      main: '#dc004e',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
    text: {
      primary: '#1a1a1a',
      secondary: '#666666',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: { fontWeight: 700, letterSpacing: '-0.02em' },
    h6: { fontWeight: 700 },
    subtitle1: { fontWeight: 600 },
    body2: { color: 'rgba(0,0,0,0.7)' },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        // Prevent iOS zoom on input focus
        '@media (max-width: 768px)': {
          'input[type="text"], input[type="email"], input[type="password"], input[type="search"], input[type="tel"], input[type="url"], input[type="number"], textarea, select': {
            fontSize: '16px !important',
            transform: 'translateZ(0)',
            WebkitAppearance: 'none',
            borderRadius: 0,
          },
        },
        // Prevent zoom on all inputs on mobile devices
        '@supports (-webkit-touch-callout: none)': {
          'input, textarea, select': {
            fontSize: '16px !important',
          },
        },
        // Additional mobile-specific fixes
        body: {
          WebkitTextSizeAdjust: '100%',
          WebkitTapHighlightColor: 'transparent',
          touchAction: 'manipulation',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 14px 0 rgba(0,0,0,0.08)',
          borderRadius: 12,
          backgroundColor: '#ffffff',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 8,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 8, fontWeight: 600 },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& input': {
            '@media (max-width: 768px)': {
              fontSize: '16px !important',
            },
            '@supports (-webkit-touch-callout: none)': {
              fontSize: '16px !important',
            },
          },
          '& textarea': {
            '@media (max-width: 768px)': {
              fontSize: '16px !important',
            },
            '@supports (-webkit-touch-callout: none)': {
              fontSize: '16px !important',
            },
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          color: '#1a1a1a',
          boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
        },
      },
    },
  },
});

// Dark theme configuration
const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    // GitHub Dark / Primer colors
    primary: {
      main: '#58a6ff',      // GitHub blue
      light: '#79c0ff',
      dark: '#1f6feb',
    },
    secondary: {
      main: '#f78166',      // GitHub coral
    },
    background: {
      default: '#0d1117',   // GitHub page background
      paper: '#161b22',     // GitHub cards/panels
    },
    text: {
      primary: '#c9d1d9',   // GitHub primary text
      secondary: '#8b949e', // GitHub muted text
    },
    divider: '#30363d',
    success: { main: '#2ea043' }, // GitHub green
    warning: { main: '#d29922' }, // GitHub yellow
    error: { main: '#f85149' },   // GitHub red
    info: { main: '#388bfd' },
    action: {
      hover: '#21262d',
      selected: '#30363d',
      disabled: 'rgba(201,209,217,0.38)',
      disabledBackground: 'rgba(48,54,61,0.5)',
      focus: 'rgba(88,166,255,0.4)',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: { fontWeight: 700, letterSpacing: '-0.02em' },
    h6: { fontWeight: 700 },
    subtitle1: { fontWeight: 600 },
    body2: { color: '#8b949e' }, // GitHub muted text
  },
  shape: { borderRadius: 10 }, // GitHub uses slightly smaller radius
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        // Prevent iOS zoom on input focus
        '@media (max-width: 768px)': {
          'input[type="text"], input[type="email"], input[type="password"], input[type="search"], input[type="tel"], input[type="url"], input[type="number"], textarea, select': {
            fontSize: '16px !important',
            transform: 'translateZ(0)',
            WebkitAppearance: 'none',
            borderRadius: 0,
          },
        },
        // Prevent zoom on all inputs on mobile devices
        '@supports (-webkit-touch-callout: none)': {
          'input, textarea, select': {
            fontSize: '16px !important',
          },
        },
        // Additional mobile-specific fixes
        body: {
          WebkitTextSizeAdjust: '100%',
          WebkitTapHighlightColor: 'transparent',
          touchAction: 'manipulation',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: '#161b22',
          border: '1px solid #30363d',
          borderRadius: 10,
          boxShadow: 'none', // GitHub uses minimal shadows
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 8,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { 
          borderRadius: 8, 
          fontWeight: 600,
          backgroundColor: '#21262d',
          color: '#c9d1d9',
          border: '1px solid #30363d',
        },
        colorPrimary: {
          backgroundColor: 'rgba(88,166,255,0.15)',
          color: '#58a6ff',
          border: '1px solid rgba(88,166,255,0.3)',
        },
        colorSuccess: {
          backgroundColor: 'rgba(46,160,67,0.15)',
          color: '#2ea043',
          border: '1px solid rgba(46,160,67,0.3)',
        },
        colorWarning: {
          backgroundColor: 'rgba(210,153,34,0.15)',
          color: '#d29922',
          border: '1px solid rgba(210,153,34,0.3)',
        },
        colorError: {
          backgroundColor: 'rgba(248,81,73,0.15)',
          color: '#f85149',
          border: '1px solid rgba(248,81,73,0.3)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        colorPrimary: {
          backgroundColor: '#0d1117',
          borderBottom: '1px solid #30363d',
          boxShadow: 'none',
          color: '#c9d1d9',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: '#161b22',
          border: '1px solid #30363d',
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          backgroundColor: '#161b22',
          border: '1px solid #30363d',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: '#21262d',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: '#30363d',
          color: '#c9d1d9',
        },
        head: {
          backgroundColor: '#21262d',
          color: '#c9d1d9',
          fontWeight: 600,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:nth-of-type(odd)': {
            backgroundColor: 'rgba(48,54,61,0.3)',
          },
          '&:hover': {
            backgroundColor: '#21262d',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& input': {
            '@media (max-width: 768px)': {
              fontSize: '16px !important',
            },
            '@supports (-webkit-touch-callout: none)': {
              fontSize: '16px !important',
            },
          },
          '& textarea': {
            '@media (max-width: 768px)': {
              fontSize: '16px !important',
            },
            '@supports (-webkit-touch-callout: none)': {
              fontSize: '16px !important',
            },
          },
        },
      },
    },
  },
});

export const ThemeProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isSystemPreference, setIsSystemPreference] = useState(true);

  // Function to get system preference
  const getSystemPreference = () => {
    return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  };

  // Initialize theme based on system preference or localStorage
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme-preference');
    
    if (savedTheme) {
      // User has a saved preference
      setIsSystemPreference(savedTheme === 'system');
      if (savedTheme === 'dark') {
        setIsDarkMode(true);
      } else if (savedTheme === 'light') {
        setIsDarkMode(false);
      } else {
        // System preference
        setIsDarkMode(getSystemPreference());
      }
    } else {
      // Default to light mode instead of system preference
      setIsDarkMode(false);
      setIsSystemPreference(false);
      localStorage.setItem('theme-preference', 'light');
    }
  }, []);

  // Listen for system theme changes when using system preference
  useEffect(() => {
    if (!isSystemPreference) return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      setIsDarkMode(e.matches);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [isSystemPreference]);

  // Update CSS custom properties for components that don't use Material-UI
  useEffect(() => {
    const root = document.documentElement;
    
    if (isDarkMode) {
      // GitHub Dark / Primer CSS custom properties
      root.style.setProperty('--bg-primary', '#0d1117');    // GitHub page background
      root.style.setProperty('--bg-secondary', '#161b22');  // GitHub cards/panels
      root.style.setProperty('--bg-tertiary', '#21262d');   // GitHub hover/selected
      root.style.setProperty('--text-primary', '#c9d1d9');  // GitHub primary text
      root.style.setProperty('--text-secondary', '#8b949e'); // GitHub muted text
      root.style.setProperty('--text-tertiary', '#6e7681'); // GitHub disabled text
      root.style.setProperty('--border-color', '#30363d');  // GitHub border
      root.style.setProperty('--shadow-color', 'rgba(1,4,9,0.85)'); // GitHub shadow
      root.style.setProperty('--hover-bg', '#21262d');      // GitHub hover
      root.style.setProperty('--primary-color', '#58a6ff'); // GitHub blue
      root.style.setProperty('--success-color', '#2ea043'); // GitHub green
      root.style.setProperty('--warning-color', '#d29922'); // GitHub yellow
      root.style.setProperty('--error-color', '#f85149');   // GitHub red
      document.body.style.backgroundColor = '#0d1117';
      document.body.style.color = '#c9d1d9';
      document.documentElement.setAttribute('data-theme', 'dark');
    } else {
      root.style.setProperty('--bg-primary', '#f5f5f5');
      root.style.setProperty('--bg-secondary', '#ffffff');
      root.style.setProperty('--bg-tertiary', '#f9f9f9');
      root.style.setProperty('--text-primary', '#1a1a1a');
      root.style.setProperty('--text-secondary', '#666666');
      root.style.setProperty('--text-tertiary', '#999999');
      root.style.setProperty('--border-color', 'rgba(0,0,0,0.12)');
      root.style.setProperty('--shadow-color', 'rgba(0,0,0,0.1)');
      root.style.setProperty('--hover-bg', 'rgba(0,0,0,0.04)');
      document.body.style.backgroundColor = '#f5f5f5';
      document.body.style.color = '#1a1a1a';
      document.documentElement.setAttribute('data-theme', 'light');
    }
  }, [isDarkMode]);

  const toggleTheme = () => {
    const newMode = !isDarkMode;
    setIsDarkMode(newMode);
    setIsSystemPreference(false);
    localStorage.setItem('theme-preference', newMode ? 'dark' : 'light');
  };

  const setSystemPreference = () => {
    setIsSystemPreference(true);
    setIsDarkMode(getSystemPreference());
    localStorage.setItem('theme-preference', 'system');
  };

  const setLightMode = () => {
    setIsDarkMode(false);
    setIsSystemPreference(false);
    localStorage.setItem('theme-preference', 'light');
  };

  const setDarkMode = () => {
    setIsDarkMode(true);
    setIsSystemPreference(false);
    localStorage.setItem('theme-preference', 'dark');
  };

  const currentTheme = isDarkMode ? darkTheme : lightTheme;

  const value = {
    isDarkMode,
    isSystemPreference,
    theme: currentTheme,
    toggleTheme,
    setSystemPreference,
    setLightMode,
    setDarkMode,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
